import { debounce } from 'lodash';
import Head from 'next/head';
import React, { useEffect, useState } from 'react';
import { TailSpin } from 'react-loader-spinner';
import styled from 'styled-components';
import useSWR from 'swr';
import { NextSeo } from 'next-seo';
import {
  WHITE_LABEL_MARKET,
  WHITE_LABEL_TWITTER,
  WHITE_LABEL_URL,
} from '../../../../lib/white-label';
import { fetcher } from '../../../../api/bootstrap';
import { HeadingOne, Subheading } from '../../../../components/Text';
import { TidalButton } from '../../../../components/common/button';
import TidalInputField from '../../../../components/common/input/TidalInputField';
import ShopFilterModal from '../../../../components/shop/ShopFilterModal';
import TidalModal from '../../../../components/common/modal/TidalModal';
import { ShopItemCard } from '../../../../components/shop';
import { Box } from '../../../../components/Flex';
import { useScrollBlock } from '../../../../utils/UseScrollBlock';
import { LOCAL_STORAGE } from '../../../../constants';
import { QuestionMarkCircleIcon } from '@heroicons/react/solid';
import dynamic from 'next/dynamic';
import { analytics } from '../../../../lib/analytics';

const ShopTour = dynamic(() => import('../../../../components/shop/ShopTour'), {
  ssr: false,
});

const Container = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  margin-right: auto;
  margin-left: auto;
`;

export const ShopHeader = styled.div`
  display: grid;
  grid-template-columns: 8fr 2fr;
  gap: 0 8px;

  & > button {
    max-width: 160px;
    justify-self: flex-end;
  }
`;

const ShopCardGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
  grid-gap: 16px;
  @media only screen and (max-width: 460px) {
    grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  }
`;

const ShopSearchWidget = styled.div`
  display: grid;
  grid-template-columns: 3fr 1fr;
  max-width: 700px;
  grid-gap: 0 16px;
`;

const metaTitle = `Purchase digital items | ${WHITE_LABEL_MARKET}`;
const metaDescription = `Purchase digital items on ${WHITE_LABEL_MARKET}`;

const shopUrl = '/market/shop/items';

function CategoryShop({ fallbackData, categorySlug }) {
  const [minPrice, setMinPrice] = useState(0);
  const [maxPrice, setMaxPrice] = useState(200);
  const [category, setCategory] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [tags, setTags] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Tour
  const [tourSteps, setTourSteps] = useState([]);
  const [tourStep, setTourStep] = useState(0);
  const [isTourOpen, setTourOpen] = useState(false);
  const [blockScroll, allowScroll] = useScrollBlock();

  useEffect(() => {
    analytics.page('Shop', categorySlug);
  }, []);

  const { data, isValidating: loading } = useSWR(
    `${shopUrl}?priceLow=${minPrice}&priceHigh=${maxPrice}&categorySlug=${categorySlug}&tags=${tags
      .map((t) => t.label)
      .join(',')}&search=${searchInput}`,
    fetcher,
    { fallbackData: !searchInput ? fallbackData : [] },
  );

  const { data: tagsData } = useSWR('/market/shop/tags', fetcher);

  const closeTour = () => {
    localStorage.setItem(
      isModalOpen
        ? LOCAL_STORAGE.HAS_TOURED_SHOP_FILTER
        : LOCAL_STORAGE.HAS_TOURED_SHOP_PAGE,
      'true',
    );
    setTourOpen(false);
    setTourStep(0);
    window.scroll({ top: 0, left: 0, behavior: 'smooth' });
    allowScroll();
  };

  const getTourSteps = () => {
    let steps;

    if (!isModalOpen) {
      steps = [
        {
          selector: '[data-tour="step-1"]',
          content: <p>Search shops by name</p>,
        },
        {
          selector: '[data-tour="step-2"]',
          content: <p>Filter shops by tags and pricing</p>,
        },
        {
          selector: '[data-tour="step-6"]',
          content: <p>View available items</p>,
        },
      ];
    } else {
      steps = [
        {
          selector: '[data-tour="step-3"]',
          content: <p>Filter shops by price</p>,
        },
        {
          selector: '[data-tour="step-4"]',
          content: <p>Filter shops with tag(s)</p>,
        },
        {
          selector: '[data-tour="step-5"]',
          content: <p>Apply shop filters to see the filtered content.</p>,
        },
      ];
    }
    if (document) {
      for (let i = 0; i < steps.length; i++) {
        const step = steps[i];

        const element = document.querySelector(step.selector);
        console.log(element);
        if (element && element.offsetParent === null) {
          steps = steps.filter((el) => el.selector != step.selector);
        } else {
          // @ts-ignore
          steps[i].position = 'left';
        }
      }
    }
    return steps;
  };

  useEffect(() => {
    setTourSteps(getTourSteps());
  }, [isModalOpen]);

  useEffect(() => {
    if (!loading) {
      if (!isModalOpen) {
        const hasTouredShopPage = localStorage.getItem(
          LOCAL_STORAGE.HAS_TOURED_SHOP_PAGE,
        );
        if (!hasTouredShopPage) {
          setTourOpen(true);
          blockScroll();
        }
      } else {
        const hasTouredShopPageFilter = localStorage.getItem(
          LOCAL_STORAGE.HAS_TOURED_SHOP_FILTER,
        );

        if (!hasTouredShopPageFilter) {
          setTimeout(() => {
            setTourOpen(true);
            blockScroll();
          }, 500);
        }
      }
    }
  }, [loading, isModalOpen]);

  return (
    <>
      <Container>
        <Head>
          <title>Shop - {WHITE_LABEL_MARKET}</title>
          <meta property="og:url" content={`${WHITE_LABEL_URL}/shop`} />
          <meta
            property="og:image"
            content={`${process.env.BASE_URL}/_next/image?url=%2Fimages%2Fmeta.png&q=50&w=3840`}
          />
          <meta name="twitter:site" content={WHITE_LABEL_TWITTER} />
          <meta name="twitter:title" content={metaTitle} />
          <meta name="twitter:description" content={metaDescription} />
          <meta
            name="twitter:image"
            content={`${process.env.BASE_URL}/_next/image?url=%2Fimages%2Fmeta.png&w=3840&q=50`}
          />
          <meta name="twitter:card" content="summary_large_image" />
        </Head>
        <NextSeo title={metaTitle} description={metaDescription} />
        <HeadingOne>Shop</HeadingOne>
        <ShopHeader>
          <Subheading>
            Our library of digital items available to purchase from verified
            sellers.
          </Subheading>
          <div className="flex flex-row">
            <QuestionMarkCircleIcon
              onClick={() => {
                setTourOpen(true);
                blockScroll();
              }}
              className="mr-2 h-10 w-10 text-gray-400 bg-gray-100 p-2 rounded-lg flex-col cursor-pointer"
            />
            <div data-tour="step-2" className="w-full">
              <TidalButton
                type="invert"
                text="Filter"
                onClick={() => setIsModalOpen(true)}
                tint={false}
              />
            </div>
          </div>
        </ShopHeader>

        <ShopSearchWidget data-tour="step-1">
          <TidalInputField
            label=""
            onChange={debounce((e: any) => {
              setSearchInput(e.target.value);
            }, 500)}
            placeholder="Search by name..."
          />
          {loading && (
            <TailSpin
              wrapperClass="my-auto"
              width={32}
              height={32}
              color="#1CBAF0"
            />
          )}
        </ShopSearchWidget>
        <Box height={20} />
        <TidalModal
          Modal={
            <ShopFilterModal
              // categories={categoryData}
              minPrice={minPrice}
              maxPrice={maxPrice}
              tags={tags}
              tagsData={tagsData}
              onClose={() => {
                setIsModalOpen(false);
              }}
              applyFilters={(
                categoryId: string,
                tags: any[],
                minPrice: number,
                maxPrice: number,
              ) => {
                setCategory(categoryId);
                setMinPrice(minPrice);
                setMaxPrice(maxPrice);
                setTags(tags);
                setIsModalOpen(false);
              }}
            />
          }
          isOpen={isModalOpen}
          onClose={() => {
            setIsModalOpen(false);
          }}
        />
        <ShopCardGrid data-tour="step-6" className="py-4">
          {data.map((item, key) => (
            <ShopItemCard
              categorySlug={categorySlug}
              slug={item.slug}
              key={key}
              name={item.name}
              imageUrl={item.imageUrl}
              tags={item.tags}
              category={item.category}
              inventory={item.inventory}
            />
          ))}
        </ShopCardGrid>
        <Box height={20} />
      </Container>
      <ShopTour
        steps={tourSteps}
        open={isTourOpen}
        setOpen={(open) => {
          if (!open) {
            closeTour();
          }
        }}
        startStep={tourStep}
      />
    </>
  );
}

export async function getServerSideProps(ctx) {
  const { categorySlug } = ctx.params;
  try {
    const data = await fetcher(`${shopUrl}?categorySlug=${categorySlug}`);
    return { props: { categorySlug, fallbackData: data } };
  } catch (e) {
    return { props: { categorySlug, fallbackData: [] } };
  }
}

export default CategoryShop;
